import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, MapPin, Users, Loader, Home } from 'lucide-react';
import { apartmentService } from '../../services/api';
import { Apartment } from '../../types';
import toast from 'react-hot-toast';

// Helper pour obtenir la photo principale
const getMainPhoto = (apt: Apartment) => {
  if (apt.photo_principale) return apt.photo_principale;
  if (apt.photos && apt.photos.length > 0) {
    const principale = apt.photos.find(p => p.is_principale);
    return principale || apt.photos[0];
  }
  return null;
};

const Apartments: React.FC = () => {
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApartments();
  }, []);

  const fetchApartments = async () => {
    try {
      const response = await apartmentService.getMyApartments();
      setApartments(response.data.apartments || []);
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer cet appartement ?')) return;
    try {
      await apartmentService.delete(id);
      toast.success('Appartement supprimé');
      setApartments(apartments.filter(a => a.id !== id));
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatPrice = (price: number) => new Intl.NumberFormat('fr-FR').format(price);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-heading font-bold">Mes appartements</h1>
        <Link to="/gerant/apartments/new" className="btn btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un appartement
        </Link>
      </div>

      {apartments.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Plus className="w-8 h-8 text-primary-500" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Aucun appartement</h3>
          <p className="text-neutral-500 mb-6">Commencez par ajouter votre premier appartement.</p>
          <Link to="/gerant/apartments/new" className="btn btn-primary">
            Ajouter mon premier appartement
          </Link>
        </div>
      ) : (
        <div className="grid gap-4">
          {apartments.map((apt) => (
            <div key={apt.id} className="card p-4 flex flex-col sm:flex-row gap-4">
              {/* Photo */}
              <div className="w-full sm:w-32 h-24 bg-neutral-200 rounded-lg overflow-hidden flex-shrink-0">
                {(() => {
                  const mainPhoto = getMainPhoto(apt);
                  if (mainPhoto) {
                    return (
                      <img
                        src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}${mainPhoto.url}`}
                        alt={apt.titre}
                        className="w-full h-full object-cover"
                      />
                    );
                  }
                  return (
                    <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                      <Home className="w-8 h-8 text-primary-400" />
                    </div>
                  );
                })()}
              </div>

              {/* Infos */}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg truncate">{apt.titre}</h3>
                <div className="flex items-center text-sm text-neutral-500 mt-1">
                  <MapPin className="w-4 h-4 mr-1" />
                  {apt.ville}
                </div>
                <div className="flex items-center gap-4 mt-2 text-sm">
                  <span className="flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    {apt.capacite} pers.
                  </span>
                  <span className="font-semibold text-primary-600">
                    {formatPrice(apt.prix_par_nuit)} FCFA/nuit
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex sm:flex-col gap-2 flex-shrink-0">
                <Link
                  to={`/apartments/${apt.id}`}
                  className="btn btn-outline btn-sm"
                  title="Voir"
                >
                  <Eye className="w-4 h-4" />
                </Link>
                <Link
                  to={`/gerant/apartments/${apt.id}/edit`}
                  className="btn btn-outline btn-sm"
                  title="Modifier"
                >
                  <Edit className="w-4 h-4" />
                </Link>
                <button
                  onClick={() => handleDelete(apt.id)}
                  className="btn btn-outline btn-sm text-red-500 hover:bg-red-50"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Apartments;
